import React from "react";

import { InputAdornment } from "@mui/material";

import { FieldParameter } from "@fartherfinance/frontend/api/TaskWorkflows/Types";

import { FieldEnhancer } from "../types";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "../FieldEnhancements.module.css";

interface StaticEnumEnhancerConfig<
  T extends Record<string, any> = Record<string, any>
> {
  enumKind: string;
  IconComponent: React.ComponentType<T>;
  createIconProps: (value: string) => T;
  // Optional: function to add grouping metadata to options
  addGroupMetadata?: (option: { value: string; label: string }) => {
    group: string;
  };
}

/**
 * Factory function to create enhancers for StaticEnum fields with icons
 * Reduces code duplication and ensures consistency
 */
export function createStaticEnumEnhancer<T extends Record<string, any>>({
  enumKind,
  IconComponent,
  createIconProps,
  addGroupMetadata,
}: StaticEnumEnhancerConfig<T>): FieldEnhancer {
  return {
    matches: (fieldParameter: FieldParameter) => {
      return Boolean(
        fieldParameter.dataType?.type === "EnumData" &&
          fieldParameter.dataType.resourceType?.type === "StaticEnum" &&
          fieldParameter.dataType.resourceType.kinds?.includes(enumKind)
      );
    },

    getConfig: (currentValue: string | undefined) => ({
      renderOption: (props, option) => {
        const iconProps = createIconProps(option.value);

        return (
          <li
            {...props}
            className={toClassName(props.className, styles.enhancedOption)}
          >
            <IconComponent {...iconProps} />
            <Text variant="body">{option.label}</Text>
          </li>
        );
      },

      renderStartAdornment: currentValue
        ? () => {
            const iconProps = createIconProps(currentValue);

            return (
              <InputAdornment position="start">
                <IconComponent {...iconProps} />
              </InputAdornment>
            );
          }
        : undefined,

      // Add grouping functionality if metadata function is provided
      ...(addGroupMetadata && {
        enrichOptions: (options) => {
          return options.map((option) => ({
            ...option,
            meta: addGroupMetadata(option),
          }));
        },
        getGroupBy: () => (option) => option.meta.group,
      }),
    }),
  };
}
